import * as React from "react";
import { Route, RouteComponentProps, Switch } from "react-router-dom";
import { RoleAssignments } from "@app/user/RoleAssignments";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import { ClientCreation } from "@app/admin/client-administration/ClientCreation";
import { ClientDefaultRoles } from "@app/admin/client-administration/ClientDefaultRoles";
import { ClientDetail } from "@app/admin/client-administration/ClientDetail";
import { ClientList } from "@app/admin/client-administration/ClientList";
import { ClientLicenses, ClientLicenseDetails } from "@app/admin/client-license";
import { AdminDashboard } from "@app/admin/Dashboard";
import { InternalReports } from "@app/admin/internal-reports/InternalReports";
import { LoginStatistics } from "@app/admin/login-statistics/LoginStatistics";
import { AddSpecialty } from "@app/admin/manage-specialties/AddSpecialty";
import { Specialties } from "@app/admin/manage-specialties/Specialties";
import { Taxonomies } from "@app/admin/manage-taxonomies/Taxonomies";
import { Templates } from "@app/admin/manage-templates/Templates";
import ProviderResults from "@app/admin/provider-results/ProviderResults";
import { RoleGroups } from "@app/admin/role-groups/RoleGroups";
import { SoftwareReleasesList, SoftwareReleaseDetail } from "@app/admin/software-releases";
import { EditTutorials } from "@app/admin/tutorials";
import { BulkUserCreate } from "@app/admin/user-administration/BulkUserCreate";
import { UserDetails } from "@app/admin/user-administration/UserDetails";
import { UsersList } from "@app/admin/user-administration/UserList";
import { UserSessions } from "@app/admin/user-sessions/UserSessions";
import { NotFound } from "@app/shared/ErrorPages";
import SpecialtyGroupException from "@app/admin/specialty-group-exception/CmsTemplate";
import { ViewManifest } from "@app/admin/view-manifest";

interface ClientAdminRouteProps {
  clientId?: string;
  userId?: string;
}

type Props = RouteComponentProps<ClientAdminRouteProps>;

export const AdminRouter: React.FC<React.PropsWithChildren<Props>> = () => {
  const user = React.useContext(UserContext);

  if (!user) {
    return <NotFound />;
  }

  return (
    <Switch>
      {(user.roles.includes(Roles.QUEST_ADMIN) || user.roles.includes(Roles.CLIENT_ADMIN)) ? (
        [
          <Route key="dashbard" path="/admin/dashboard" component={AdminDashboard} />,
          <Route exact={true} key="client-admin" path="/admin/client-administration" component={ClientList} />,
          <Route key="client-admin-create" path="/admin/client-administration/create" component={ClientCreation} />,
          <Route key="client-default-roles" path="/admin/client-administration/client-default-roles" component={ClientDefaultRoles} />,
          <Route exact={true} key="client-detail" path="/admin/client-administration/:clientId" component={ClientDetail} />,
          <Route key="client-role-groups" path="/admin/client-administration/:clientId/role-groups" component={RoleGroups} />,

          <Route key="role-groups" path="/admin/role-groups" component={RoleGroups} />,

          <Route exact={true} key="templates" path="/admin/manage-templates" component={Templates} />,
          <Route key="add-specialty" path="/admin/manage-templates/manage-specialties/specialties/:templateCollectionId/:templateId/add-specialty" component={AddSpecialty} />,
          <Route exact={true} key="specialties" path="/admin/manage-templates/manage-specialties/specialties/:templateCollectionId/:templateId" component={Specialties} />,
          <Route key="taxonomies" path="/admin/manage-taxonomies/:templateCollectionId/:templateId" component={Taxonomies} />,

          <Route exact={true} key="users-list" path="/admin/user-administration" component={UsersList} />,
          <Route key="user-details" path="/admin/user-administration/create" component={UserDetails} />,
          <Route key="user-details" path="/admin/user-administration/user/:userId" component={UserDetails} />,
          <Route key="bulk-user-create" path="/admin/user-administration/bulk-create" component={BulkUserCreate} />,

          <Route key="user-sessions" path="/admin/user-sessions" render={(renderProps) => <UserSessions user={user} {...renderProps} />} />,
          <Route key="provider-results" path="/admin/provider-results" component={ProviderResults} />,

          <Route key="manage-tutorials" path="/admin/manage-tutorials" component={EditTutorials} />,

          <Route key="client-login-statistics" path="/admin/login-statistics/:clientId" component={LoginStatistics} />,
          <Route key="login-statistics" path="/admin/login-statistics" component={LoginStatistics} />,

          <Route exact={true} key="software-releases" path="/admin/software-releases" component={SoftwareReleasesList} />,
          <Route key="software-release-detail" path="/admin/software-releases/:softwareReleaseId" component={SoftwareReleaseDetail} />,
          <Route key="client-licenses" path="/admin/client-license/manager" component={ClientLicenses} />,
          <Route key="client-license-details" path="/admin/client-license/:clientId" component={ClientLicenseDetails} />,

          <Route exact={true} key="internal-reports" path="/admin/internal-reports/" component={InternalReports} />,
          <Route key="role-assignments-client" path="/admin/internal-reports/role-assignments/:clientId" component={RoleAssignments} />,
          <Route key="role-assignments" path="/admin/internal-reports/role-assignments/" component={RoleAssignments} />,

          <Route key="specialty-group-exception" path="/admin/specialty-group-exception" component={SpecialtyGroupException} />,
        ]
      ) : (
        null
      )}

      {(user.roles.includes(Roles.QUEST_INTERNAL_REPORTS)) ? (
        <Route exact={true} path="/admin/internal-reports/" component={InternalReports} />
      ) : (
        null
      )}

      {(user.roles.includes(Roles.NJDOBI_SELF_SERVICE_FUNCTIONS)) ? (
        <Route exact={true} path="/admin/view-manifest" component={ViewManifest} />
      ) : (
        null
      )}

      <Route component={NotFound} />
    </Switch>
  );
}
